<template>
    <div class="CriterionNumberInput">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionEnumInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <FormInput
                :invalid="showValidationErrors && (showError || accessed) && hasError"
                force-error-slot
                force-action-slot
            >
                <input
                    :type="temporaryString ? 'text' : 'number'"
                    :value="viewValue"
                    :min="form.schema.minimum"
                    :max="form.schema.maximum"
                    :disabled="disabled || rbacDisabled"
                    @input="onInput"
                    @blur="touch"
                    @focus="onFocus"
                    @change="onChange"
                >
                <template v-if="showValidationErrors && (showError || accessed) && error" #errorMessage>
                    {{ error }}
                </template>
                <template v-if="deletable || rbacDisabled" #actionButtons>
                    <LockInputIcon v-if="rbacDisabled" />
                    <NewButton
                        v-else-if="deletable"
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <slot name="deleteIcon">
                            <EidIcon :icon-src="faXmark" alt="delete" />
                        </slot>
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component } from '@evidentid/vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { numberToString } from '@evidentid/dashboard-commons/utils/numericUtilities';
    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaInteger, JsonSchemaNumber } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewButton from '@/components/new-button/NewButton.vue';
    import AbstractCriterionInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/AbstractCriterionInput';

    type JsonFormNumeric = JsonFormBase<JsonSchemaNumber> | JsonFormBase<JsonSchemaInteger>;

    @Component({
        emits: [ 'change', 'delete', 'input' ],
        components: { EidIcon, FormInput, FontAwesomeIcon, FormElement, LockInputIcon, NewButton },
    })
    export default class CriterionNumberInput extends AbstractCriterionInput<JsonFormNumeric, number> {
        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;

        private get viewValue(): string {
            return this.value == null ? '' : typeof this.value === 'number' ? numberToString(this.value) : this.value;
        }

        // Used to decide if the input shouldn't be temporarily without [type=number] to show actual value
        protected get temporaryString(): boolean {
            const exponentRegex = /[eE]/;
            return isNaN(this.viewValue.replace(exponentRegex, 'x') as any) &&
                this.viewValue.trim().length > 0;
        }

        private onInput(event: InputEvent): void {
            if (!this.disabled) {
                const inputValue = (event.target as HTMLInputElement).value;
                this.$emit('input', this.form.getValue(inputValue));
            }
        }

        private onChange(event: Event): void {
            if (!this.disabled) {
                const inputValue = (event.target as HTMLInputElement).value;
                this.$emit('change', this.form.getValue(inputValue));
            }
        }
    }
</script>
